import { debounce } from '../utils';

// Style constants - matching scss class names
const DISMISS_CLASS = 'glossary-dialog--dismiss';
const DIALOG_CLOSING = 'glossary-dialog--closing';
const DIALOG_OPEN = 'glossary-dialog--open';
const DIALOG_ACTIVE = 'is-active';
const DIALOG_HIDDEN = 'u-hidden';
const DIALOG_ABOVE = 'glossary-dialog--above';
const DIALOG_BELOW = 'glossary-dialog--below';
const DIALOG_TAIL = 'glossary-dialog--tail';
// Misc constants
const ARIA_HIDDEN = 'aria-hidden';
const ESC_KEY = 'Escape';
const SPACE_KEY = 'Space';
const ENTER_KEY = 'Enter';

// Util
function titleCase(str) {
    return str.toLowerCase().split(' ').map((word) => {
        return word.replace(word[0], word[0].toUpperCase());
    }).join(' ');
}

function tooltipMarkup(term, definition) {
    return `
        <div class="glossary-dialog--inner">
            <div class="glossary-dialog--actions">
                <button
                    class="${ DISMISS_CLASS }"
                    title="Close dialog"
                    aria-label='close dialog'
                >
                    <svg 
                        fill="none" 
                        viewBox="0 0 29 29">
                        <title>X shape to close dialog</title>
                        <path 
                            fill="#000" 
                            d="M11.01 10.303a.5.5 0 10-.707.707l4.596 4.597-4.596 4.596a.5.5 0 10.707.707l4.597-4.596 4.596 4.596a.5.5 0 10.707-.707l-4.596-4.596 4.596-4.597a.5.5 0 00-.707-.707L15.607 14.9l-4.597-4.596z" 
                        />
                    </svg>
                </button>
            </div>
        
            <div class="glossary-dialog--content">
                <h4>${ titleCase(term) }</h4>
                <div class="glossary-dialog--content-definition">
                    ${ definition }
                </div>
            </div>    
        </div>
        
        <div class="glossary-dialog--tail"></div>
    `;
}


class Glossary {
    constructor(elm, glossaryDetail) {
        // style like a button, work with keyboard navigation
        elm.classList.add('glossary-tooltip');
        elm.setAttribute('role', 'button');
        elm.setAttribute('tabindex', '0');
        elm.setAttribute('title', `Show definition of ${ glossaryDetail.term }`);
        elm.setAttribute('aria-label', `Show definition of ${ glossaryDetail.term }`);

        this.isOpen = false; // default to closed
        // set class attrs
        this.id = elm.dataset.glossaryTermId;
        this.term = glossaryDetail.term;
        this.definition = glossaryDetail.definition;
        this.triggerNode = elm;
        // set actions
        this.container = this.constructContainer();
        this.closeButton = this.container.querySelector(`.${ DISMISS_CLASS }`);
        this.tailEl = this.container.querySelector(`.${ DIALOG_TAIL }`);
        this.registerEventHandlers(); // #shoudvebeenabutton
        this.isFocused = false;
    }

    registerEventHandlers() {
        // Close - click close button
        if (this.closeButton) {
            this.closeButton.addEventListener('click', (event) => {
                event.stopPropagation();
                this.hide();
            });
        }

        const self = this;
        // Close - a click outside of the dialog
        window.addEventListener('click', debounce((event) => {
            if (event.target !== self.container) {
                self.hide();
            }
        }, 300));

        // Close - Escape key pressed, close all
        document.addEventListener('keydown', debounce((ev) => {
            if (ev.code === ESC_KEY) {
                self.hide();
            }
        }, 300));

        // listen if tabbed away from an open dialog trigger (usually previous tabbable element)
        self.triggerNode.addEventListener('blur', (event) => {
            if (!self.triggerNode.contains(event.relatedTarget)) {
                if (self.isFocused) {
                    self.isFocused = false;
                    self.hide();
                }
            }
        });

        // listen if tabbed away from an open dialog child element (usually tab past the last item in the dialog)
        const navigableElementsInTriggerNode = this.triggerNode.querySelectorAll('a[href], button');
        navigableElementsInTriggerNode.forEach(elm => {
            elm.addEventListener('blur', ev => {
                if (self.isFocused) {
                    if (!self.triggerNode.contains(ev.relatedTarget)) {
                        self.isFocused = false;
                        self.hide();
                    }
                }
            });
        });

        if (this.triggerNode) {
            // Open - click
            this.triggerNode.addEventListener('click', (event) => {
                event.stopPropagation();
                this.show();
            });

            // register the focus of the trigger
            this.triggerNode.addEventListener('focus', (event) => {
                this.isFocused = true;
            });

            // handle keyboad actions for the trigger
            this.triggerNode.addEventListener('keydown', debounce((ev) => {
                if (this.isFocused && (ev.code === SPACE_KEY || ev.code === ENTER_KEY)) {
                    ev.preventDefault();
                    this.show();
                }
            }, 300));
        }
    }

    constructContainer() {
        const container = document.createElement('div');
        const containerTitle = `Definition of '${ this.term }'`;
        container.classList.add('glossary-dialog', DIALOG_HIDDEN);
        container.innerHTML = tooltipMarkup(this.term, this.definition);
        container.setAttribute('role', 'dialog');
        container.setAttribute(ARIA_HIDDEN, 'true');
        container.setAttribute('title', containerTitle);
        this.triggerNode.setAttribute('aria-label', containerTitle);
        this.triggerNode.appendChild(container);
        return container;
    }

    show() {
        if (this.isOpen) {
            return;
        }
        this.container.classList.remove(DIALOG_HIDDEN);
        this.container.setAttribute(ARIA_HIDDEN, 'false');
        this.place(); // must be done after removing the DIALOG_HIDDEN class to get the elements width
        this.isOpen = true;
    }
    hide() {
        if (this.isOpen === false) {
            return;
        }
        this.container.classList.add(DIALOG_CLOSING);
        this.container.classList.remove(DIALOG_OPEN);
        // note: even with arrow function this is becoming global window, so hacking that in here
        const self = this;
        setTimeout(() => {
            // allow transition out to occur before removing
            self.container.classList.remove(DIALOG_CLOSING);
            self.container.setAttribute(ARIA_HIDDEN, 'true');
            self.triggerNode.classList.remove(DIALOG_ACTIVE);
            self.container.classList.add(DIALOG_HIDDEN);
            self.isOpen = false;
        }, 600);
    }

    place() {
        // reset the container from any previous placements
        this.container.classList.remove(DIALOG_ABOVE, DIALOG_BELOW);
        // set variables
        const containerWidth = this.container.offsetWidth;
        const containerHeight = this.container.offsetHeight;

        const triggerBox = this.triggerNode.getBoundingClientRect();
        const triggerCenterX = triggerBox.width / 2;

        const verticalOffset = 11;
        const minPadding = 10;

        // determine if should display above or below
        const placeAbove = triggerBox.top > (containerHeight + verticalOffset + minPadding);
        const placement = placeAbove ? 'above' : 'below';
        const placementClasses = {
            'above': DIALOG_ABOVE,
            'below': DIALOG_BELOW,
        };
        this.container.classList.add(placementClasses[placement]);

        // calculate top
        const top = (
            placeAbove
                ? -1 * (containerHeight + verticalOffset)
                : triggerBox.height + verticalOffset
        );
        this.container.style.top = `${ top.toFixed(2) }px`;

        // calculate X
        const winCenterX = document.documentElement.clientWidth / 2;
        const anchorLeft = triggerBox.left < winCenterX;

        const maxAbsX = (document.documentElement.clientWidth - (containerWidth + minPadding));
        const baseLeft = triggerCenterX - (containerWidth / 2);
        const minRelativeLeft = -(triggerBox.left - minPadding); // don't place off LHS screen edge
        const maxRelativeLeft = maxAbsX - triggerBox.left; //  don't place off RHS screen edge

        const left = (
            anchorLeft
                ? Math.max(baseLeft, minRelativeLeft)
                : Math.min(baseLeft, maxRelativeLeft)
        );

        this.container.style.left = `${ left.toFixed(2) }px`;

        // calculate tail position
        if (this.tailEl) {
            const tailBaseLeft = triggerCenterX - left;
            const tailMaxLeft = containerWidth - 20;
            const tailMinLeft = 20;
            const tailLeft = Math.max(tailMinLeft, Math.min(tailMaxLeft, tailBaseLeft));
            this.tailEl.style.left = `${ tailLeft }px`;
        }
    }
} // end class


export default function setup(glossaryElms) {
    // Collate all glossary elms for the page, get glossary detail then create Glossary instances.
    const glossaryTermIDs = glossaryElms.map(elm => elm.dataset.glossaryTermId);
    const qstr = [...new Set(glossaryTermIDs)].toString(); // set de-duplicates the array

    fetch(`/public-glossary/terms/?gids=${ qstr }`, {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json; charset=utf-8',
        },
    })
        .then(res => {
            return res.json();
        })
        .then(jsonResp => {
            return glossaryElms.forEach(el => {
                const glossaryElmDetail = jsonResp.filter(gitem => {
                    return gitem['glossary-term-id'].toString() === el.dataset.glossaryTermId;
                });
                if (glossaryElmDetail[0]) {
                    // eslint-disable-next-line no-unused-vars
                    const glossary = new Glossary(el, glossaryElmDetail[0]);
                }
            });
        })
        .catch((error) => {
            console.log('error: ', error);
        });
}
